import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../auth.service';
import { EmbedLoginService, ThemeService } from '@core/services';
import { environment } from 'environments/environment';
import { Title } from '@angular/platform-browser';
import { APPS, SERVICES } from '@core/assets/constants';
import { Subscription } from 'rxjs';

const { contentful } = environment;

/**
 * OTP code verification screen (second factor) shown after a login that requires
 * two-factor authentication. Handles digit-by-digit entry of the code, resending
 * with a cooldown, and the limit of failed attempts.
 */
@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss']
})
export class OtpComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('otpInput') public otpElementList: QueryList<ElementRef>;

  public isContentfulActive = contentful.active;
  public serviceName = 'campus';
  public attemptsLeft = 3;
  public resendCooldown = 0;
  public expirySeconds = 600;
  public otpError: any;
  public supportUrl: string;
  private credentials: any;
  private cooldownTimer: ReturnType<typeof setInterval>;
  private validOtpSubscription: Subscription = new Subscription();
  private resendOtpSubscription: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private embedLoginService: EmbedLoginService,
    private themeService: ThemeService,
    private translate: TranslateService,
    private titleService: Title
  ) {}

  /**
   * Retrieves the pending login's credentials from the navigation state, applies
   * the service's theme and starts the resend cooldown.
   */
  public ngOnInit(): void {
    this.credentials = this.route.snapshot.queryParamMap.get('token');
    this.serviceName = SERVICES[this.route.snapshot.queryParamMap.get('sid')] || 'campus';
    this.themeService.setTheme(this.serviceName);

    this.startResendCooldown();
    this.supportUrl = this.translate.instant('urls.forgotEmailSupport');
    this.titleService.setTitle(`${this.translate.instant('otp.title')} | Game Strategies`);
  }

  /**
   * Places focus on the first digit input once the view is ready.
   */
  public ngAfterViewInit(): void {
    setTimeout(() => this.focusInput(0));
  }

  /**
   * Stops the cooldown timer and clears the displayed error.
   */
  public ngOnDestroy(): void {
    clearInterval(this.cooldownTimer);
    this.otpError = null;
    this.validOtpSubscription.unsubscribe();
    this.resendOtpSubscription.unsubscribe();
  }

  /**
   * Indicates whether all 6 OTP code inputs have a value entered.
   */
  public get hasOtpValid(): boolean {
    return this.otpElementList?.toArray().every((el) => !!el.nativeElement.value) ?? false;
  }

  /**
   * Handles entry of a digit in an OTP code input, including arrow-key navigation
   * and backspace deletion to the previous input.
   * @param event Keyboard event captured on the input
   * @param index Position of the input within the code sequence
   */
  public onDigitInput(event: KeyboardEvent, index: number): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;

    if (/^[^\d\s]$/.test(event.key)) {
      const digit = event.key.replace(/\D/g, '').slice(-1);
      input.value = isFinite(Number(input.value)) ? input.value : digit;
      return;
    }

    if (/^\d$/.test(event.key)) {
      const digit = event.key.replace(/\D/g, '').slice(-1);
      input.value = digit;
      this.focusInput(index + 1);
    } else if (event.key === 'Backspace') {
      const inputElementList = this.otpElementList.toArray();
      if (inputElementList[index]) {
        inputElementList[index].nativeElement.value = '';
        (event.target as HTMLInputElement).value = '';
        this.focusInput(index - 1);
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.focusInput(index - 1);
    } else if (event.key === 'ArrowRight' && index < 5) {
      this.focusInput(index + 1);
    }
  }

  /**
   * Distributes the OTP code pasted from the clipboard across the digit inputs.
   * @param event Paste event captured on the first input
   */
  public onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    const inputElementList = this.otpElementList.toArray();
    text.split('').forEach((char, i) => {
      if (inputElementList[i]) inputElementList[i].nativeElement.value = char;
      this.focusInput(i + 1);
    });
  }

  /**
   * Submits the entered OTP code for validation. If it fails, deducts an attempt
   * and redirects to the login once the available attempts are exhausted.
   */
  public submit(): void {
    const inputValueList = this.otpElementList.toArray().map((input) => input.nativeElement.value);
    const otpCode = inputValueList.join('');
    if (otpCode.length !== 6) return;
    this.otpError = null;

    this.validOtpSubscription = this.authService
      .validateOtpCode(this.credentials, otpCode)
      .subscribe({
        next: (response: any) => {
          this.embedLoginService.onLoginSuccess(response);
        },
        error: (error: any) => {
          this.attemptsLeft = error.payload?.remainingAttempts;
          this.clearDigits();

          this.otpError = {
            errorCode: `apiErrors.${error.errorCode || 'OTP_INVALID'}`,
            message: error.message || this.translate.instant('otp.otpCodeError')
          };

          if (error?.statusCode === 403) {
            this.backToLogin();
            return;
          }
          setTimeout(() => this.focusInput(0));
        }
      });
  }

  /**
   * Returns to the login screen of the current service.
   */
  public backToLogin(): void {
    this.router
      .navigate(['../login'], {
        relativeTo: this.route,
        queryParams: { sid: APPS[this.serviceName] }
      })
      .then();
  }

  /**
   * Requests a new OTP code, resetting attempts, digits and the resend cooldown.
   */
  public resendOtp(): void {
    if (this.resendCooldown > 0) return;

    this.resendOtpSubscription = this.authService.resendOtpCode(this.credentials).subscribe({
      next: (response: any) => {
        this.attemptsLeft = 3;
        this.expirySeconds = 600;
        this.otpError = null;
        this.credentials = response;
        this.clearDigits();
        this.startResendCooldown();
        setTimeout(() => this.focusInput(0));
      }
    });
  }

  /**
   * Places focus on the digit input at the given position, if it exists.
   * @param index Position of the input within the code sequence
   */
  private focusInput(index: number): void {
    this.otpElementList.toArray()[index]?.nativeElement.focus();
  }

  /**
   * Clears the value of all OTP code digit inputs.
   */
  private clearDigits(): void {
    this.otpElementList.toArray().forEach((input) => (input.nativeElement.value = ''));
  }

  /**
   * Starts (or resets) the 60s cooldown timer to allow a new resend of the OTP code.
   */
  private startResendCooldown(): void {
    this.resendCooldown = 60;
    clearInterval(this.cooldownTimer);
    this.cooldownTimer = setInterval(() => {
      if (this.resendCooldown > 0) {
        this.resendCooldown--;
      } else {
        clearInterval(this.cooldownTimer);
      }
    }, 1000);
  }
}
